import { getSupabase } from '@/lib/supabaseClient';
import { notificationService } from './notificationService';

const STREAK_TYPES = {
  daily_spending: {
    label: 'Daily Spending Log',
    goal: 30,
    icon: 'DollarSign',
    color: 'text-green-500',
    bgColor: 'bg-green-50'
  },
  weekly_goals: {
    label: 'Weekly Goal Review',
    goal: 12,
    icon: 'Target',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50'
  },
  monthly_budget: {
    label: 'Monthly Budget Planning',
    goal: 6,
    icon: 'Calendar',
    color: 'text-purple-500',
    bgColor: 'bg-purple-50'
  },
  credit_check: {
    label: 'Credit Report Check',
    goal: 4,
    icon: 'FileCheck',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50'
  },
  daily_login: {
    label: 'Daily Login',
    goal: 30,
    icon: 'LogIn',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50'
  },
  daily_tips: {
    label: 'Daily Tips',
    goal: 30,
    icon: 'Lightbulb',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50'
  },
  account_age: {
    label: 'Account Age',
    goal: 365,
    icon: 'UserCheck',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50'
  },
  spending_logged: {
    label: 'Spending Logged',
    goal: 30,
    icon: 'DollarSign',
    color: 'text-green-500',
    bgColor: 'bg-green-50'
  },
  lesson_completed: {
    label: 'Lesson Completed',
    goal: 20,
    icon: 'BookOpen',
    color: 'text-pink-500',
    bgColor: 'bg-pink-50'
  }
};

const STREAK_REWARDS = {
  7: '7-Day Streak Badge',
  30: '30-Day Streak Badge',
  90: '3-Month Streak Badge',
  180: '6-Month Streak Badge',
  365: '1-Year Streak Badge'
};

export const REGISTRATION_REWARD = 'Welcome Aboard Badge';

export const ACCOUNT_AGE_REWARDS = {
  7: 'One-Week Member Badge',
  30: 'One-Month Member Badge',
  90: 'Three-Month Member Badge',
  180: 'Six-Month Member Badge',
  365: 'One-Year Member Badge'
};

class StreakService {
  constructor() {
    this.supabase = getSupabase();
    this.goalCache = {};
    this.streakCache = new Map();
    this.cacheTtlMs = 60 * 1000; // 60s cache for read operations
  }

  async getStreakData(userId, streakType) {
    const cacheKey = `${userId}:${streakType}`;
    const cached = this.streakCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < this.cacheTtlMs) {
      return cached.data;
    }

    const { data, error } = await this.supabase
      .from('user_streaks')
      .select('current_streak, longest_streak, updated_at, last_activity_date')
      .eq('user_id', userId)
      .eq('streak_type', streakType)
      .maybeSingle();

    if (error) {
      // No rows (PGRST116) should be treated as no data, not an error
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    this.streakCache.set(cacheKey, { data, ts: Date.now() });
    return data;
  }

  async updateStreak(userId, streakType) {
    const streakData = await this.getStreakData(userId, streakType);
    const now = new Date();
    const lastActivity = streakData?.last_activity_date ? new Date(streakData.last_activity_date) : null;
    
    let currentStreak = streakData?.current_streak || 0;
    let longestStreak = streakData?.longest_streak || 0;
    let shouldIncrement = false;

    // Check if the streak should be incremented based on streak type
    switch (streakType) {
      case 'daily_spending':
        shouldIncrement = !lastActivity || this.isNextDay(lastActivity, now);
        break;
      case 'weekly_goals':
        shouldIncrement = !lastActivity || this.isNextWeek(lastActivity, now);
        break;
      case 'monthly_budget':
        shouldIncrement = !lastActivity || this.isNextMonth(lastActivity, now);
        break;
      case 'credit_check':
        shouldIncrement = !lastActivity || this.isNextMonth(lastActivity, now);
        break;
      default:
        shouldIncrement = false;
    }

    if (shouldIncrement) {
      currentStreak++;
      longestStreak = Math.max(currentStreak, longestStreak);

      // Check for streak rewards
      if (STREAK_REWARDS[currentStreak]) {
        await this.awardStreakReward(userId, streakType, currentStreak);
      }

      // Update streak data
      const { data, error } = await this.supabase
        .from('user_streaks')
        .upsert({
          user_id: userId,
          streak_type: streakType,
          current_streak: currentStreak,
          longest_streak: longestStreak,
          updated_at: now.toISOString(),
          last_activity_date: now.toISOString().split('T')[0]
        }, { onConflict: 'user_id,streak_type' })
        .select()
        .single();

      if (error) throw error;
      // Invalidate cache for this streak on write
      this.streakCache.delete(`${userId}:${streakType}`);
      return data;
    }

    return streakData;
  }

  async awardStreakReward(userId, streakType, currentStreak) {
    const rewardName = STREAK_REWARDS[currentStreak];
    if (!rewardName) return;

    // Check if reward already exists
    const { data: existingReward } = await this.supabase
      .from('user_rewards')
      .select('id')
      .eq('user_id', userId)
      .eq('reward_type', streakType)
      .eq('reward_name', rewardName)
      .maybeSingle();

    if (existingReward) return;

    // Award the reward using RPC to handle conflicts
    const { error: rewardError } = await this.supabase.rpc(
      'award_streak_reward',
      {
        user_id: userId,
        reward_type: streakType,
        reward_name: rewardName
      }
    );

    if (rewardError) throw rewardError;

    // Send notification
    await notificationService.sendNotification(userId, 'streak_milestone', {
      reward_name: rewardName,
      streak_type: STREAK_TYPES[streakType].label
    });
  }

  async awardSingleUseReward(userId, rewardType, rewardName) {
    if (!userId) return;

    const { data: existingReward } = await this.supabase
      .from('user_rewards')
      .select('id')
      .eq('user_id', userId)
      .eq('reward_type', rewardType)
      .eq('reward_name', rewardName)
      .maybeSingle();

    if (existingReward) return;

    const { error } = await this.supabase
      .from('user_rewards')
      .insert({
        user_id: userId,
        reward_type: rewardType,
        reward_name: rewardName
      });

    if (error) throw error;
  }

  isNextDay(lastDate, currentDate) {
    const lastUTC = Date.UTC(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
    const currUTC = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const diffDays = Math.round((currUTC - lastUTC) / 86400000);
    return diffDays === 1;
  }

  isNextWeek(lastDate, currentDate) {
    const last = new Date(lastDate);
    const current = new Date(currentDate);
    const daysDiff = Math.floor((current - last) / (1000 * 60 * 60 * 24));
    return daysDiff >= 7 && daysDiff <= 8;
  }

  isNextMonth(lastDate, currentDate) {
    const last = new Date(lastDate);
    const current = new Date(currentDate);
    return (
      (current.getFullYear() === last.getFullYear() &&
        current.getMonth() - last.getMonth() === 1) ||
      (current.getFullYear() - last.getFullYear() === 1 &&
        current.getMonth() === 0 &&
        last.getMonth() === 11)
    );
  }

  async getUserRewards(userId) {
    const { data, error } = await this.supabase
      .from('user_rewards')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async getUserGoal(userId, streakType) {
    const { data, error } = await this.supabase
      .from('user_streak_goals')
      .select('goal_days')
      .eq('user_id', userId)
      .eq('streak_type', streakType)
      .maybeSingle();

    if (error) throw error;
    return data?.goal_days;
  }

  async getUserGoals(userId) {
    const { data, error } = await this.supabase
      .from('user_streak_goals')
      .select('streak_type, goal_days')
      .eq('user_id', userId);

    if (error) throw error;
    const goals = {};
    data?.forEach(row => {
      goals[row.streak_type] = row.goal_days;
    });
    return goals;
  }

  async setUserGoal(userId, streakType, goalDays) {
    const { data, error } = await this.supabase
      .from('user_streak_goals')
      .upsert({ user_id: userId, streak_type: streakType, goal_days: goalDays }, { onConflict: 'user_id,streak_type' })
      .select()
      .single();

    if (error) throw error;
    if (!this.goalCache[userId]) {
      this.goalCache[userId] = {};
    }
    this.goalCache[userId][streakType] = goalDays;
    return data;
  }

  async getStreakConfig(streakType, userId) {
    let config = STREAK_TYPES[streakType];
    if (!config) return null;

    config = { ...config };

    if (userId) {
      try {
        if (!this.goalCache[userId]) {
          this.goalCache[userId] = await this.getUserGoals(userId);
        }
        const goal = this.goalCache[userId][streakType];
        if (goal !== undefined && goal !== null) {
          config.goal = goal;
        }
      } catch (error) {
        console.error('Error fetching user streak goal:', error);
      }
    }

    return config;
  }

  getAllStreakTypes() {
    return STREAK_TYPES;
  }
}

export const streakService = new StreakService(); 
